import React, { useState } from "react";
import Swal from "sweetalert2";
import { createBookClass } from "../services/api";
import type { classDataProps } from "../types/common";
import {
  canSubmitNewClass,
  extractApiErrorDetail,
  generateClassCode,
  isDuplicateClassName,
  normalizeCreatedClass,
} from "../utils/classCreation";

// 建立新班級 Modal(kanban 106957)
// 自 BookPreview「派出作文」的建班 Popup 抽出共用:批次上傳精靈步驟 1 也要能建班。
// 樣式、欄位與錯誤提示沿用原 Popup(含 isNeedInvition === "Y" 書籍的開通碼欄)。
// 以條件渲染掛載({show && <CreateClassModal/>}),關閉即卸載、欄位自然歸零。
interface CreateClassModalProps {
  bookId: string;
  // 書籍 isNeedInvition === "Y" 時需輸入業務發放的開通碼(作為班級 _id)
  needActivation: boolean;
  teacherId: string;
  teacherEmail: string;
  teacherName: string;
  // 該書現有班級:後端建班不查重名,同書同名班會在 batch 端共用容器、
  // 批改中顯示互相污染,送出前以此清單擋重名(code review 106957)
  existingClasses: ReadonlyArray<{ className: string }>;
  // 建立成功時回傳正規化後的班級(保證帶 bookID,能通過精靈的 classesForUpload 過濾)
  onCreated: (cls: classDataProps) => void;
  onClose: () => void;
}

export const CreateClassModal: React.FC<CreateClassModalProps> = ({
  bookId,
  needActivation,
  teacherId,
  teacherEmail,
  teacherName,
  existingClasses,
  onCreated,
  onClose,
}) => {
  const [className, setClassName] = useState("");
  const [activationCode, setActivationCode] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const canSubmit = canSubmitNewClass(needActivation, className, activationCode);

  const handleCreate = async () => {
    if (!canSubmit || isCreating) return;
    // 同書重名擋下(後端不查重):modal 不關、欄位保留,改名即可重送
    if (isDuplicateClassName(existingClasses, className)) {
      Swal.fire({
        icon: "error",
        title: "班級名稱重複",
        text: "此書籍已有同名班級,請改用其他名稱",
        confirmButtonColor: "#2D5A7B",
      });
      return;
    }
    setIsCreating(true);
    try {
      // classCode 只是 API 必填參數;實際邀請碼由後端自產 8 碼數字,以回傳為準
      const response = await createBookClass(
        bookId,
        teacherId,
        teacherEmail,
        teacherName,
        className.trim(),
        generateClassCode(),
        needActivation ? activationCode.trim() : undefined
      );
      const created = normalizeCreatedClass(response.data, {
        bookID: bookId,
        className: className.trim(),
      });
      if (!created) {
        // 後端沒回邀請碼:不能拿前端自產碼頂替顯示(學生加不進來),當建立失敗處理
        throw new Error("班級建立回應缺少邀請碼");
      }
      onCreated(created);
      onClose();
    } catch (error) {
      // 失敗(重名、開通碼錯、網路):提示後 modal 不關、欄位保留,可直接重試(邊界 B2)
      console.error("建立班級失敗:", error);
      if (needActivation) {
        Swal.fire({
          icon: "error",
          title: "建立班級失敗",
          text: "請輸入正確的開通碼。(提醒您：一組開通碼僅可開通一次)",
          confirmButtonText: "重新輸入",
          confirmButtonColor: "#2D5A7B",
        });
      } else {
        // 帶出後端 detail:getBooks 失敗的 fail-open 死角下,開通碼書的老師
        // 才看得到「開通碼」相關的真正原因,而不是誤導的通用文案
        Swal.fire({
          icon: "error",
          title: "建立班級失敗",
          text: extractApiErrorDetail(error) || "請稍後再試",
          confirmButtonColor: "#2D5A7B",
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-sm overflow-hidden">
        {/* 標題欄 */}
        <div className="bg-[#2D5A7B] py-4 px-6">
          <h2 className="text-xl font-bold text-white text-center">建立新班級</h2>
        </div>
        {/* 內容區 */}
        <div className="p-6 flex flex-col gap-4">
          {needActivation && (
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600">邀請碼</label>
              <input
                type="text"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                placeholder="請輸入開通碼"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            {needActivation && (
              <label className="text-sm text-gray-600">班級名稱</label>
            )}
            <input
              type="text"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              placeholder="3年一班"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
          {/* 按鈕區 */}
          <div className="flex gap-4 mt-2 justify-center">
            <button
              onClick={onClose}
              disabled={isCreating}
              className="px-8 py-2 border-2 border-[#2D5A7B] text-[#2D5A7B] rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleCreate}
              disabled={!canSubmit || isCreating}
              className="px-8 py-2 bg-[#2D5A7B] text-white rounded-full hover:bg-[#1e4a63] transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {isCreating ? "建立中..." : "確定"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
